import { openableURL } from './frameDocument';
import { MAX_LINK_TEXT } from './linkText';

/**
 * # The isolated message frame (ADR 0078)
 *
 * When the deployment has a user-content origin, a message body is not a
 * `srcdoc` document of this app at all: it is a page the user-content origin
 * serves, framed from there. That origin holds nothing — no cookie of ours,
 * no device key, no service worker, no offline cache — and the document is
 * sandboxed into an OPAQUE origin on top of that, so even a script that got
 * past the sanitizer, the CSP and the sandbox would run somewhere with nothing
 * to read and nothing to act as.
 *
 * The price is reach: the page can no longer look into the frame, so the frame
 * runs one script of ours (served by the backend, allowed by its hash and
 * nothing else — `backend/internal/usercontent/frame.js`) that reports its
 * height, hands link activations to the page and says which link is hovered.
 * Every message from it is untrusted data: this module checks its exact shape,
 * clamps its numbers and re-validates every URL before anything uses it.
 *
 * This is the ONE module that names the sandbox tokens of that frame, so the
 * srcdoc renderer's tripwire (MessageFrame.test.tsx: its modules never contain
 * the script token) still holds for them, and a second tripwire holds here:
 * this file never contains the same-origin token. The two must never meet in
 * one frame: script plus same-origin is a full sandbox escape.
 */

/**
 * The sandbox of the isolated frame, identical to the `sandbox` directive the
 * user-content origin serves the document under (usercontent.DocumentSandbox),
 * so it holds from both sides:
 *
 *  - `allow-scripts`: for our frame script only; the document's CSP admits
 *    exactly its hash, so nothing else can run.
 *  - `allow-popups` and `allow-popups-to-escape-sandbox`: a link that opens
 *    natively (the frame script not running) opens as a normal, unrelated tab
 *    (ADR 0052).
 *
 * Deliberately absent: the same-origin token. Its absence is what makes the
 * document's origin opaque — `event.origin` is the string "null" on every
 * message it posts — rather than the user-content origin.
 */
export const ISOLATED_FRAME_SANDBOX_TOKENS: readonly string[] = [
  'allow-scripts',
  'allow-popups',
  'allow-popups-to-escape-sandbox',
];

export const ISOLATED_FRAME_SANDBOX = ISOLATED_FRAME_SANDBOX_TOKENS.join(' ');

/** The protocol tags on both directions (frame.js). */
export const FRAME_PROTOCOL = 'frame/v1';
export const PARENT_PROTOCOL = 'parent/v1';

/**
 * The frame protocol revision this page speaks, advertised to frame.js in the
 * frame URL's fragment (`#c=<channel>&v=2`; see `frameURL`).
 *
 * The two sides ship separately: the app shell the service worker cached can
 * be older or newer than frame.js on the content origin during a deploy, and
 * this page drops any message whose key set it does not know. So the frame
 * adds a field only when the page has said it reads it, and the page still
 * accepts the v1 shape from an older frame:
 *
 *  - v2: `open` may carry `text`, the link's text, and a same-document link
 *    posts `fragment` with where its target is (ADR 0101).
 */
export const FRAME_PROTOCOL_VERSION = 2;

/** The frame URL for a minted render: its channel and our protocol revision in the fragment. */
export function frameURL(mintedURL: string, channel: string): string {
  return `${mintedURL}#c=${channel}&v=${FRAME_PROTOCOL_VERSION}`;
}

/** How many URLs a preview may carry, and how long one may be (frame.js). */
export const MAX_PREVIEW_URLS = 20;
export const MAX_FRAME_HREF = 4096;
/** A height the frame reports is clamped here too, before the page's own clamp. */
const MAX_REPORTED_HEIGHT = 1_000_000;

/** A fresh channel id: 32 characters of base64url from 24 random bytes. */
export function newChannel(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export type FrameMessage =
  | { type: 'size'; height: number; zoom: number; scrollX: boolean; wide: boolean }
  | { type: 'loaded'; images: number; failed: number }
  | { type: 'open'; url: URL; text?: string }
  | { type: 'mailto'; href: string }
  | { type: 'preview'; urls: URL[] }
  | { type: 'fragment'; at: number };

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function finite(value: unknown, min: number, max: number): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.min(max, Math.max(min, value));
}

function count(value: unknown): number | null {
  const n = finite(value, 0, 100_000);
  return n === null ? null : Math.floor(n);
}

/**
 * Validates one message from the frame. Returns null for anything that is not
 * exactly one of the shapes, on this channel: extra keys, a wrong type,
 * a non-finite number, a URL `openableURL` refuses. The caller has already
 * checked that it came from the frame's window.
 */
export function parseFrameMessage(data: unknown, channel: string): FrameMessage | null {
  if (!isPlainRecord(data)) return null;
  if (data.ols !== FRAME_PROTOCOL || data.c !== channel || typeof data.type !== 'string') return null;
  const keys = Object.keys(data).sort().join(',');
  switch (data.type) {
    case 'size': {
      if (keys !== 'c,height,ols,scrollX,type,wide,zoom') return null;
      const height = finite(data.height, 0, MAX_REPORTED_HEIGHT);
      const zoom = finite(data.zoom, 0.01, 1);
      if (height === null || zoom === null) return null;
      if (typeof data.scrollX !== 'boolean' || typeof data.wide !== 'boolean') return null;
      return { type: 'size', height, zoom, scrollX: data.scrollX, wide: data.wide };
    }
    case 'loaded': {
      if (keys !== 'c,failed,images,ols,type') return null;
      const images = count(data.images);
      const failed = count(data.failed);
      if (images === null || failed === null) return null;
      return { type: 'loaded', images, failed: Math.min(failed, images) };
    }
    case 'open': {
      // v1 (no text) from an older frame.js, or v2 with the link's text.
      const withText = keys === 'c,href,ols,text,type';
      if (keys !== 'c,href,ols,type' && !withText) return null;
      if (typeof data.href !== 'string' || data.href.length > MAX_FRAME_HREF) return null;
      if (withText && (typeof data.text !== 'string' || data.text.length > MAX_LINK_TEXT)) return null;
      const url = openableURL(data.href);
      if (!url) return null;
      return withText && data.text !== '' ? { type: 'open', url, text: data.text as string } : { type: 'open', url };
    }
    case 'mailto': {
      if (keys !== 'c,href,ols,type') return null;
      if (typeof data.href !== 'string' || data.href.length > MAX_FRAME_HREF) return null;
      const href = data.href.trim();
      return /^mailto:/i.test(href) ? { type: 'mailto', href } : null;
    }
    case 'preview': {
      if (keys !== 'c,ols,type,urls') return null;
      if (!Array.isArray(data.urls) || data.urls.length > MAX_PREVIEW_URLS) return null;
      const urls: URL[] = [];
      for (const raw of data.urls as unknown[]) {
        if (typeof raw !== 'string' || raw.length > MAX_FRAME_HREF) return null;
        const url = openableURL(raw);
        if (!url) return null;
        if (!urls.some((seen) => seen.href === url.href)) urls.push(url);
      }
      return { type: 'preview', urls };
    }
    case 'fragment': {
      // Where a same-document link's target is: a fraction of the body.
      if (keys !== 'at,c,ols,type') return null;
      const at = finite(data.at, 0, 1);
      return at === null ? null : { type: 'fragment', at };
    }
    default:
      return null;
  }
}

/** The one message the page sends the frame. */
export function fitMessage(channel: string, mode: 'width' | 'actual'): Record<string, string> {
  return { ols: PARENT_PROTOCOL, c: channel, type: 'fit', mode };
}

/**
 * An origin as the browser serializes one, or null: https, or http on a
 * loopback host (local development), with no path, query or credentials.
 */
export function normalizeOrigin(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.username || url.password || url.search || url.hash) return null;
  if (url.pathname !== '/' && url.pathname !== '') return null;
  if (url.protocol === 'https:') return url.origin;
  if (url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)) {
    return url.origin;
  }
  return null;
}

/**
 * The origin this build's app shell allows framing (index.html's
 * `<meta name="ols-content-origin">`, written at build time from
 * VITE_CONTENT_ORIGIN, the same value the static server's `frame-src` is
 * built from). Empty or absent: this build frames nothing.
 */
export function buildContentOrigin(doc: Document = document): string | null {
  const meta = doc.querySelector('meta[name="ols-content-origin"]');
  return normalizeOrigin(meta?.getAttribute('content'));
}

let warnedMismatch = false;

/**
 * Whether this page may frame message bodies from `sessionOrigin`, and from
 * where. Every condition must hold, or the answer is null and the srcdoc
 * renderer is used, as before ADR 0078:
 *
 *  - the server advertises an origin;
 *  - it is exactly the one this build's CSP allows framing (otherwise the
 *    frame would be refused anyway, as a blank message);
 *  - it is not this page's own origin (then it would isolate nothing);
 *  - nothing on this page has already seen the frame refused (`forceLegacy`).
 */
export function resolveContentOrigin(
  sessionOrigin: string | null | undefined,
  buildOrigin: string | null = buildContentOrigin(),
  pageOrigin: string = window.location.origin,
): string | null {
  if (legacyForced) return null;
  const session = normalizeOrigin(sessionOrigin);
  if (session === null) return null;
  if (buildOrigin === null || session !== buildOrigin || session === pageOrigin) {
    if (!warnedMismatch) {
      warnedMismatch = true;
      console.warn(
        `Message isolation is off: the server's user-content origin (${session}) is not the ` +
          `one this build allows framing (${buildOrigin ?? 'none'}). Rebuild the app with ` +
          'VITE_CONTENT_ORIGIN set to the same value.',
      );
    }
    return null;
  }
  return session;
}

let legacyForced = false;

/**
 * The page's own CSP refused the frame (a service worker still serving an
 * index.html from before the origin was configured, say): stop trying for the
 * life of this page and render in the srcdoc frame instead.
 */
export function forceLegacy(): void {
  legacyForced = true;
}

export function isLegacyForced(): boolean {
  return legacyForced;
}

/** Tests only. */
export function resetIsolationForTests(): void {
  legacyForced = false;
  warnedMismatch = false;
}

/**
 * Throws unless the isolated frame is exactly what it must be before it is
 * pointed anywhere: the sandbox is exactly the isolated list, and the URL is
 * on the user-content origin, which is not this page's.
 */
export function assertIsolatedFrame(frame: HTMLIFrameElement, src: string, contentOrigin: string): void {
  const tokens = (frame.getAttribute('sandbox') ?? '').split(/\s+/).filter(Boolean);
  const exact =
    tokens.length === ISOLATED_FRAME_SANDBOX_TOKENS.length &&
    tokens.every((token, i) => token === ISOLATED_FRAME_SANDBOX_TOKENS[i]);
  if (!exact) {
    throw new Error(
      `MessageFrame: refusing to load. sandbox is [${tokens.join(', ')}], ` +
        `must be exactly [${ISOLATED_FRAME_SANDBOX_TOKENS.join(', ')}].`,
    );
  }
  let origin: string;
  try {
    origin = new URL(src).origin;
  } catch {
    throw new Error('MessageFrame: refusing to load a frame URL that does not parse.');
  }
  if (origin !== contentOrigin || origin === window.location.origin) {
    throw new Error(`MessageFrame: refusing to load ${origin}: not the user-content origin.`);
  }
}

/**
 * Plain text for a body that cannot be framed right now (offline, or the
 * render could not be made): the text part, or the sanitized HTML's text
 * content from an inert DOMParser document — which runs nothing and loads
 * nothing, and is read only as text.
 */
export function plainTextOf(bodyText: string | undefined, bodyHtml: string): string {
  if (bodyText !== undefined && bodyText.trim() !== '') return bodyText;
  try {
    const doc = new DOMParser().parseFromString(bodyHtml, 'text/html');
    return (doc.body?.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
  } catch {
    return '';
  }
}
